import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { getMetricCatalog, runMetrics, runMetricsStream, drillMetric, getBackbone, getRoles, getPeople, getRollups, getLanes, getCustomer, setSpineLevels as apiSetSpineLevels, addTerminology } from '../../api'
import { buildEffectiveFilters, MetricCard } from './MetricsTab'
import './SpineView.css'
import { providerLabel } from '../../utils/providedThrough'
import LoadError from '../../components/LoadError'
import { listFrom, loadErrorMessage } from '../../utils/loadState'

/**
 * SpineView — the "Backbone" tab (customer-facing name: Backbone; code stays SpineView).
 * Rebuild #1: the operating-model spine as the surface, per the approved mock.
 *
 *   Left  — the drawn backbone (Enterprise → Entity → Org → Role → Person) with the
 *           three domains projecting off nodes as crosswalk EDGES colored by confidence.
 *   Right — stand on a node: edges + findings LEAD (the differentiated layer), then a lean
 *           metric HEADLINE per domain, then "Explore all" → the full module→metric tree,
 *           then a metric → the drill slide-out (rendering + export + rows).
 *   Doors — Backbone ⇄ Metrics toggle (story-first vs. straight-to-numbers).
 *
 * Live now: the tree, node→scope (HCM+WFM exact, Finance crosswalk-pending), the whole
 * metric surface off /run+/drill, and real row-level drill. Curated first pass: the
 * crosswalk edges/findings (marked "asserted") — the §9 engine computes them live in #2.
 *
 * Display name: renders companyName (never the customer_id — "Demo Cascade" is banned).
 */

// ── Spine levels ──────────────────────────────────────────────────────────────
const LEVELS = [
  { key: 'enterprise', eyebrow: 'Enterprise' },
  { key: 'entity',     eyebrow: 'Entity' },
  { key: 'org',        eyebrow: 'Org unit' },
  { key: 'role',       eyebrow: 'Role' },
  { key: 'person',     eyebrow: 'Person' },
]

// ── Domain projections ────────────────────────────────────────────────────────
const DOMAINS = [
  { key: 'hcm',     name: 'HCM',     tint: '#1f9bd8' },
  { key: 'wfm',     name: 'WFM',     tint: '#7c5cf0' },
  { key: 'finance', name: 'Finance', tint: '#2e9d7e' },
]
const DOMAIN_SCOPE_KEYS = {
  hcm:     ['company', 'org_level_1', 'org_level_2', 'org_level_3', 'org_level_4', 'employee_status', 'pay_group', 'location', 'hcm_job'],
  wfm:     ['wfm_ll1', 'wfm_ll2', 'wfm_ll3', 'wfm_job'],
  finance: ['fin_period', 'fin_subsidiary', 'fin_department', 'fin_class', 'fin_accttype'],
}
const STATE = {
  confirmed:   { c: '#1f9bd8', bg: '#e2f2fb', label: 'Confirmed' },
  proposed:    { c: '#b5791e', bg: '#f6ebd6', label: 'Proposed' },
  conflicting: { c: '#c0334e', bg: '#f7e2e7', label: 'Conflicting' },
  nolink:      { c: '#8794ac', bg: '#eef1f6', label: 'No-link' },
}
const TREE_DRILL_METRIC = 'total_worked_hours'

// ── formatting ────────────────────────────────────────────────────────────────
function fmtVal(v, disp) {
  if (v === null || v === undefined) return '—'
  if (Array.isArray(v)) return disp === 'time_series' ? 'trend' : `${v.length} rows`
  if (typeof v === 'object') return '—'
  const x = Number(v); if (Number.isNaN(x)) return String(v).slice(0, 22)
  if (disp === 'percent') return `${x.toFixed(1)}%`
  if (disp === 'currency') {
    const a = Math.abs(x)
    if (a >= 1e9) return `$${(x / 1e9).toFixed(2)}B`
    if (a >= 1e6) return `$${(x / 1e6).toFixed(1)}M`
    if (a >= 1e3) return `$${(x / 1e3).toFixed(0)}K`
    return `$${x.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
  }
  const a = Math.abs(x)
  if (a >= 1e6) return `${(x / 1e6).toFixed(2)}M`
  if (a >= 1e3) return x.toLocaleString('en-US', { maximumFractionDigits: 0 })
  return `${x}`
}
function deriveEnterpriseLabel(customerId) {
  if (!customerId) return 'Enterprise'
  return customerId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}
// Cross-domain rollup formatter — keeps per-unit precision the big-tile fmtVal drops
// (e.g. $65.79/hr, $62,248/employee) rather than abbreviating to $66 / $62K.
function fmtRollup(v, unit) {
  if (v === null || v === undefined) return '—'
  const x = Number(v)
  if (Number.isNaN(x)) return '—'
  if (unit === 'currency') {
    const dp = Math.abs(x) < 1000 ? 2 : 0
    return `$${x.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`
  }
  return x.toLocaleString('en-US', { maximumFractionDigits: 1 })
}
const ROLLUP_SUFFIX = {
  labor_cost_per_worked_hour: ' / hr',
  revenue_per_employee: ' / employee',
  worked_hours_per_employee: ' hrs / employee',
}
// ── Authored-wins spine-level resolution ──────────────────────────────────────
// Merge the derived alignment (the floor) with authored spine_levels overrides.
//   • base label per level = the DERIVED canonical headline from spine_alignment
//     (e.g. "Department" when HCM/WFM/NetSuite agree; the neutral concept when they
//     diverge). Falls back to the structural eyebrow if no alignment is available.
//   • authored label_ref pins a label over the derived headline (optional refinement).
//   • authored active:false hides a level (enterprise is always kept — it's the root).
//   • each level carries its per-system row {domain,label} for the bridge render.
// Empty spine_levels + no alignment => structural eyebrows (identical to before).
export function resolveLevels(levels, spineLevels, terminology, alignment) {
  const align = {}
  ;(alignment || []).forEach(a => { if (a && a.level) align[a.level] = a })
  const authored = {}
  ;(spineLevels || []).forEach(sl => { if (sl && sl.level) authored[sl.level] = sl })
  const out = {}
  ;(levels || []).forEach(({ key, eyebrow }) => {
    const a = authored[key], al = align[key]
    const active = key === 'enterprise' ? true : (a ? a.active !== false : true)
    let label = (al && al.headline) || eyebrow          // derived headline is the floor
    if (a && a.label_ref) label = (terminology || {})[a.label_ref] || a.label_ref  // authored pin wins
    out[key] = {
      key, active, label,
      authored: !!a,
      systems: (al && al.systems) || [],
      agree: al ? al.agree : null,
    }
  })
  return out
}
function groupModulesByDomain(catalog) {
  const byDomain = { hcm: [], wfm: [], finance: [] }
  Object.entries(catalog || {}).forEach(([moduleKey, mod]) => {
    if (!mod || mod._synthetic) return
    // A module's `data_source` (from the catalog) is in the backbone's vocabulary, the same
    // keys as DOMAINS. Not `domain`: that is the business-domain axis (every wfm_* module
    // is domain=hcm), and it matched none of these after the 2026-08 re-tag.
    const d = DOMAINS.find(dd => dd.key === (mod.data_source || 'hcm'))
    if (d) byDomain[d.key].push([moduleKey, mod])
  })
  return byDomain
}
function nodeScope(node, entFinMap = {}, orgMaps = {}) {
  if (!node || node.level === 'enterprise') return {}
  if (node.level === 'entity') {
    const s = { company: [node.code], wfm_ll2: [node.code] }
    // Finance scopes to the node ONLY at entity grain, and ONLY where the org
    // crosswalk confirms an entity -> ns_subsidiary mapping. Org grain stays
    // finance-unscoped (no department crosswalk) — the honest 'pending' below.
    const sub = entFinMap && entFinMap[node.code]
    if (sub) s.fin_subsidiary = [sub]
    return s
  }
  if (node.level === 'org') {
    // An org node's code lives in whichever system built the tree (WFM LL3 when WFM
    // is present, else HCM orgLevel1). Reusing that code for BOTH domains assumes
    // HCM orgLevel1Code == WFM LL3 code — true only where the two systems number
    // departments identically. Instead: scope the tree's NATIVE domain from the
    // code, and the OTHER domain only via the backbone's name-matched crosswalk.
    // No match -> leave that domain unscoped (its 'crosswalk pending' pill shows)
    // rather than silently scoping it to a code that means nothing there.
    const w2h = orgMaps.wfmToHcm || {}
    const h2w = orgMaps.hcmToWfm || {}
    const s = {}
    if (orgMaps.system === 'hcm') {
      s.org_level_1 = [node.code]                       // native (HCM-built tree)
      if (h2w[node.code]) s.wfm_ll3 = [h2w[node.code]]  // mapped; else WFM pending
    } else {
      s.wfm_ll3 = [node.code]                           // native (WFM-built tree)
      if (w2h[node.code]) s.org_level_1 = [w2h[node.code]] // mapped; else HCM pending
    }
    return s
  }
  if (node.level === 'role') {
    // The primaryJobCode is SHARED HCM↔WFM (employment_details.primaryJobCode ==
    // wfm_persons.primaryJob, one 200-code space). A single selected code scopes
    // BOTH domains directly — no crosswalk. When an entity is in context, COMPOSE
    // entity×role: HCM+WFM gain the entity constraint, and Finance (which has no
    // role) still scopes to the entity via the finance crosswalk — so composed
    // Role scopes all three domains honestly. No entity -> company-wide (as before).
    if (!node.code) return {}
    const s = { hcm_job: [node.code], wfm_job: [node.code] }
    if (node.entity) {
      s.company = [node.entity]
      s.wfm_ll2 = [node.entity]
      const sub = entFinMap && entFinMap[node.entity]
      if (sub) s.fin_subsidiary = [sub]
    }
    return s
  }
  if (node.level === 'person') {
    // Person is a drill-only leaf with NO metric axis of its own — a person
    // inherits their org's scope so the projections stay in the person's org
    // context. Reuse the org scoping (crosswalk-resolved) via the parent code.
    if (!node.parent) return {}
    return nodeScope({ level: 'org', code: node.parent }, entFinMap, orgMaps)
  }
  return {}
}
function domainScoped(domainKey, scope) {
  return (DOMAIN_SCOPE_KEYS[domainKey] || []).some(k => scope[k] && scope[k].length > 0)
}

// ── Drawn backbone (SVG) — zoomable + pannable canvas ─────────────────────────
function Backbone({ nodes, selected, onSelect, lanes = [] }) {
  const TX = 150, TOP = 56, GAP = 132, R = 9, VBW = 880
  const isHub = Array.isArray(nodes) && nodes.length > 0
  // hub geometry: the spine is a compact left NAV ladder; capability lanes fan off a
  // SINGLE anchor — the currently selected node — so nothing implies a false
  // adjacency to a level, and the fan re-origins (and lanes re-scope) as you drill.
  const HX = 120, HUB_TOP = 44, NODE_GAP = 50, LANE_X = 240, LANE_W = 600, LANE_GAP = 12
  const laneHeight = (lane) => 62 + ((lane.contracted && lane.contracted.length) ? 18 : 0)
  const laneLayout = []
  if (isHub && Array.isArray(lanes)) { let _y = HUB_TOP; lanes.forEach(lane => { const h = laneHeight(lane); laneLayout.push({ lane, y: _y, h }); _y += h + LANE_GAP }) }
  const lanesH = laneLayout.length ? (laneLayout[laneLayout.length - 1].y + laneLayout[laneLayout.length - 1].h - HUB_TOP) : 0
  const ladderH = Math.max(0, (nodes.length - 1) * NODE_GAP)
  const nodeY = (i) => HUB_TOP + i * NODE_GAP
  const _ai = nodes.findIndex(n => n.key === selected.level)
  const anchorY = nodeY(_ai < 0 ? 0 : _ai)
  const contentH = Math.max(ladderH, lanesH || 90)
  const H = isHub ? (HUB_TOP + contentH + 46) : (TOP + GAP * (nodes.length - 1) + 44)
  const svgRef = useRef(null)
  const drag = useRef(null)
  const [cam, setCam] = useState({ k: 1, tx: 0, ty: 0 })
  const [grabbing, setGrabbing] = useState(false)
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v))
  const zoomAt = (vx, vy, f) => setCam(c => {
    const nk = clamp(c.k * f, 0.5, 4)
    return { k: nk, tx: vx - nk * ((vx - c.tx) / c.k), ty: vy - nk * ((vy - c.ty) / c.k) }
  })
  useEffect(() => {
    const svg = svgRef.current
    if (!svg) return
    const onWheel = (e) => {
      e.preventDefault()
      const r = svg.getBoundingClientRect()
      const vx = (e.clientX - r.left) / r.width * VBW
      const vy = (e.clientY - r.top) / r.height * H
      zoomAt(vx, vy, e.deltaY < 0 ? 1.12 : 1 / 1.12)
    }
    svg.addEventListener('wheel', onWheel, { passive: false })
    return () => svg.removeEventListener('wheel', onWheel)
  }, [H]) // eslint-disable-line react-hooks/exhaustive-deps
  const onDown = (e) => {
    if (e.target.closest && e.target.closest('.bb-node, .bb-edge-g')) return
    drag.current = { x: e.clientX, y: e.clientY }
    setGrabbing(true)
    svgRef.current.setPointerCapture?.(e.pointerId)
  }
  const onMove = (e) => {
    if (!drag.current) return
    const r = svgRef.current.getBoundingClientRect()
    const dx = (e.clientX - drag.current.x) / r.width * VBW
    const dy = (e.clientY - drag.current.y) / r.height * H
    drag.current = { x: e.clientX, y: e.clientY }
    setCam(c => ({ ...c, tx: c.tx + dx, ty: c.ty + dy }))
  }
  const onUp = () => { drag.current = null; setGrabbing(false) }

  // ── lane rendering (hub mode) ──
  const laneDotFill = (st) => st === 'linked' ? '#1D9E75' : st === 'needs_attention' ? '#EF9F27' : st === 'scope' ? null : '#c1c8d6'
  const cleanName = (nm) => String(nm || '').replace(/\s*\([^)]*\)/g, '').trim()
  // Hard guarantee text never spills its chip: truncate to the chip's inner width.
  const fitText = (s, w) => { const max = Math.max(5, Math.floor((w - 40) / 6.6)); s = String(s || ''); return s.length > max ? s.slice(0, max - 1) + '…' : s }
  const covText = (lane, p) => {
    // a product provided through another vendor expects no direct connection
    if (!p.coverage && p.state === 'provided_through') return `via ${providerLabel(p.provided_through)}`
    if (!p.coverage) return p.connected ? 'connected' : 'not connected'
    const c = p.coverage, unit = lane.grain === 'entity' ? (c.of === 1 ? 'subsidiary' : 'subsidiaries') : 'people'
    if (p.state === 'scope') return `${c.covered.toLocaleString()} / ${c.of.toLocaleString()} · ${(c.of - c.covered).toLocaleString()} scope`
    return `${c.covered.toLocaleString()} / ${c.of.toLocaleString()} ${unit}`
  }
  const renderLane = ({ lane, y: ly, h: lh }) => {
    const prods = lane.products || []
    const n = Math.max(prods.length, 1)
    const gap = 8, innerW = LANE_W - 28
    const chipW = Math.min(320, Math.floor((innerW - (n - 1) * gap) / n))
    const els = [
      <rect key="card" x={LANE_X} y={ly} width={LANE_W} height={lh} rx="10" fill="#ffffff" stroke="#e5e8ee" strokeWidth="1" />,
      <text key="name" x={LANE_X + 14} y={ly + 19} style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: '13.5px', fontWeight: 600, fill: '#031244' }}>{lane.label}</text>,
    ]
    if (lane.gap) {
      const bw = 152
      els.push(<rect key="gapbg" x={LANE_X + LANE_W - bw - 14} y={ly + 8} width={bw} height="17" rx="8.5" fill="#f6ebd6" />)
      els.push(<text key="gaptx" x={LANE_X + LANE_W - bw / 2 - 14} y={ly + 20} textAnchor="middle" style={{ fontSize: '10.5px', fontWeight: 600, fill: '#b5791e' }}>contracted · nothing flowing</text>)
    } else {
      els.push(<text key="meta" x={LANE_X + LANE_W - 14} y={ly + 19} textAnchor="end" style={{ fontSize: '11px', fill: '#7a86a0' }}>{prods.length} {prods.length === 1 ? 'product' : 'products'} · links at {lane.grain || 'person'}</text>)
    }
    if (prods.length === 0) {
      els.push(<text key="empty" x={LANE_X + 14} y={ly + 46} style={{ fontSize: '12px', fontStyle: 'italic', fill: '#7a86a0' }}>no system connected</text>)
    }
    prods.forEach((p, pi) => {
      const cx = LANE_X + 14 + pi * (chipW + gap), cy = ly + 26
      const fill = laneDotFill(p.state)
      // Leading status dot (never under the text); name + coverage start clear of it.
      els.push(<rect key={'ch' + pi} x={cx} y={cy} width={chipW} height="32" rx="7" fill="#ffffff" stroke="#e5e8ee" />)
      els.push(fill
        ? <circle key={'d' + pi} cx={cx + 13} cy={cy + 16} r="5" fill={fill} />
        : <circle key={'d' + pi} cx={cx + 13} cy={cy + 16} r="5" fill="#ffffff" stroke="#b5791e" strokeWidth="1.5" />)
      els.push(<text key={'nm' + pi} x={cx + 26} y={cy + 13} style={{ fontSize: '12px', fontWeight: 600, fill: '#0d1b3d' }}>{fitText(cleanName(p.name), chipW)}</text>)
      els.push(<text key={'cv' + pi} x={cx + 26} y={cy + 26} style={{ fontSize: '10.5px', fill: '#7a86a0' }}>{fitText(covText(lane, p), chipW + 20)}</text>)
    })
    if (lane.contracted && lane.contracted.length) {
      const prodC = lane.contracted.filter(c => c.type === 'product').map(c => c.name)
      const svcN = lane.contracted.filter(c => c.type === 'service').length
      let ct = prodC.join(', '); if (ct.length > 48) ct = ct.slice(0, 46) + '…'
      const svc = svcN ? ` · ${svcN} service${svcN > 1 ? 's' : ''}` : ''
      const tail = lane.gap ? '  — paid for, no data flowing' : (lane.flowing ? '  — flowing' : '')
      els.push(<text key="con" x={LANE_X + 14} y={ly + lh - 9} style={{ fontSize: '10.5px', fill: '#8794ac' }}><tspan style={{ fill: '#44506b' }}>contracted:</tspan> {ct}{svc}<tspan style={{ fill: lane.gap ? '#b5791e' : '#8794ac' }}>{tail}</tspan></text>)
    }
    return <g key={lane.key}>{els}</g>
  }

  return (
    <div className="bb-canvas">
      <div className="bb-zoombar">
        <button className="bb-zbtn" onClick={() => zoomAt(VBW / 2, H / 2, 1 / 1.2)} aria-label="Zoom out">−</button>
        <button className="bb-zbtn" onClick={() => zoomAt(VBW / 2, H / 2, 1.2)} aria-label="Zoom in">+</button>
        <button className="bb-zbtn bb-zreset" onClick={() => setCam({ k: 1, tx: 0, ty: 0 })}>Reset</button>
        <span className="bb-zpct">{Math.round(cam.k * 100)}%</span>
        <span className="bb-zhint">scroll to zoom · drag to pan</span>
      </div>
      <svg ref={svgRef} className="bb-svg" viewBox={`0 0 ${VBW} ${H}`} preserveAspectRatio="xMidYMin meet"
        role="img" aria-label="Operating-model backbone" style={{ cursor: grabbing ? 'grabbing' : 'grab' }}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
        <defs>
          <linearGradient id="bbtrunk" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#42b8fc" stopOpacity="0.85" />
            <stop offset="1" stopColor="#42b8fc" stopOpacity="0.28" />
          </linearGradient>
        </defs>
        <g transform={`translate(${cam.tx} ${cam.ty}) scale(${cam.k})`}>
          {isHub ? (
            <>
              {laneLayout.map((e, li) => {
                const cy = e.y + e.h / 2
                return <path key={'c' + li} d={`M ${HX} ${anchorY} C ${(HX + LANE_X) / 2} ${anchorY}, ${(HX + LANE_X) / 2} ${cy}, ${LANE_X} ${cy}`} fill="none" stroke="#cfd6e2" strokeWidth="1" />
              })}
              <line x1={HX} y1={HUB_TOP} x2={HX} y2={HUB_TOP + ladderH} stroke="url(#bbtrunk)" strokeWidth="2.5" />
              {nodes.map((n, i) => {
                const y = nodeY(i)
                const active = selected.level === n.key && (n.key === 'enterprise' || selected.code === n.code)
                const reachable = ['enterprise', 'entity', 'org', 'role'].includes(n.key)
                return (
                  <g key={n.key} className="bb-node" tabIndex={0} role="button" onClick={() => reachable && onSelect(n)} style={{ cursor: reachable ? 'pointer' : 'default' }}>
                    <rect x={HX - 96} y={y - 13} width={108} height={26} fill="transparent" />
                    {active && <circle cx={HX} cy={y} r="15" fill="none" stroke="#42b8fc" strokeOpacity="0.3" className="bb-pulse" />}
                    <circle cx={HX} cy={y} r={active ? 8 : 6} fill={active ? '#42b8fc' : '#ffffff'} stroke="#42b8fc" strokeWidth={active ? 0 : 1.5} />
                    {active && <circle cx={HX} cy={y} r="4" fill="#031244" />}
                    <text x={HX - 16} y={y + 4} textAnchor="end" style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: '13px', fontWeight: 600, fill: active ? '#031244' : '#7a86a0' }}>{n.eyebrow}</text>
                  </g>
                )
              })}
              {laneLayout.map(e => renderLane(e))}
              {laneLayout.length === 0 && (
                <text x={LANE_X + 14} y={anchorY + 4} style={{ fontSize: '12.5px', fontStyle: 'italic', fill: '#7a86a0' }}>
                  {lanes === null ? 'Loading systems…' : 'No systems flowing at this scope'}
                </text>
              )}
              {laneLayout.length > 0 && <>
                <circle cx={LANE_X + 4} cy={HUB_TOP + contentH + 26} r="5" fill="#1D9E75" /><text x={LANE_X + 16} y={HUB_TOP + contentH + 30} style={{ fontSize: '11px', fill: '#7a86a0' }}>linked</text>
                <circle cx={LANE_X + 90} cy={HUB_TOP + contentH + 26} r="5" fill="#ffffff" stroke="#b5791e" strokeWidth="1.5" /><text x={LANE_X + 102} y={HUB_TOP + contentH + 30} style={{ fontSize: '11px', fill: '#7a86a0' }}>scope subset (by design)</text>
                <circle cx={LANE_X + 270} cy={HUB_TOP + contentH + 26} r="5" fill="#c1c8d6" /><text x={LANE_X + 282} y={HUB_TOP + contentH + 30} style={{ fontSize: '11px', fill: '#7a86a0' }}>not connected</text>
              </>}
            </>
          ) : (
            <>
              <line x1={TX} y1={TOP} x2={TX} y2={TOP + GAP * (nodes.length - 1)} stroke="url(#bbtrunk)" strokeWidth="2.5" />
              {nodes.map((n, i) => {
                const y = TOP + i * GAP
                const active = selected.level === n.key && (n.key === 'enterprise' || selected.code === n.code)
                const reachable = ['enterprise', 'entity', 'org', 'role'].includes(n.key)
                const built = reachable || n.key === 'person'
                return (
                  <g key={n.key + (n.code || '')}>
                    <g className="bb-node" tabIndex={0} role="button" onClick={() => reachable && onSelect(n)} style={{ cursor: reachable ? 'pointer' : 'default' }}>
                      <rect x="0" y={y - 34} width={TX + 40} height="68" fill="transparent" />
                      {active && <circle cx={TX} cy={y} r={R + 10} fill="none" stroke="#42b8fc" strokeOpacity="0.3" className="bb-pulse" />}
                      <circle cx={TX} cy={y} r={active ? R + 4 : R} fill={active ? '#42b8fc' : '#ffffff'} stroke="#42b8fc" strokeWidth={active ? 0 : 1.5} />
                      <circle cx={TX} cy={y} r="4" fill={active ? '#031244' : '#42b8fc'} />
                      <text x={TX - 26} y={y - 6} textAnchor="end" className="bb-eyebrow">{n.eyebrow}</text>
                      <text x={TX - 26} y={y + 14} textAnchor="end" className={`bb-title ${active ? '' : 'dim'} ${built ? '' : 'soon'}`}>
                        {n.title}{!built ? ' ·soon' : ''}
                      </text>
                    </g>
                  </g>
                )
              })}
            </>
          )}
        </g>
      </svg>
    </div>
  )
}

// ── Drill slide-out: rendering + export + rows (live) ─────────────────────────
function DrillDrawer({ customerId, target, scope, onClose }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    if (!target) return
    let cancelled = false
    setLoading(true); setData(null)
    drillMetric(customerId, target.key, 200, null, scope)
      .then(res => { if (!cancelled) setData(res) })
      .catch(() => { if (!cancelled) setData({ rows: [], columns: [] }) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [customerId, target, JSON.stringify(scope)])

  if (!target) return null
  const rows = data?.rows || []
  const columns = data?.columns || (rows[0] ? Object.keys(rows[0]) : [])
  const total = data?.total ?? rows.length

  const exportCsv = () => {
    if (!rows.length) return
    const head = columns.join(',')
    const body = rows.map(r => columns.map(c => JSON.stringify(r[c] ?? '')).join(',')).join('\n')
    const blob = new Blob([head + '\n' + body], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = `${target.key}.csv`; a.click()
    URL.revokeObjectURL(url)
  }
  const exportExcel = async () => {
    if (!rows.length) return
    const XLSX = await import('xlsx')
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Detail')
    XLSX.writeFile(wb, `${target.key}.xlsx`)
  }

  return (
    <>
      <div className="bb-scrim open" onClick={onClose} />
      <aside className="bb-drill open">
        <div className="bb-drill-head">
          <div>
            <div className="bb-drill-eyebrow">Metric · rendering + export</div>
            <div className="bb-drill-title">{target.label}</div>
            <div className="bb-drill-dom">
              <span className="bb-dot" style={{ background: target.tint }} /> {target.domain} · {target.module} · scoped {target.node}
            </div>
          </div>
          <button className="bb-drill-x" onClick={onClose}>✕</button>
        </div>
        <div className="bb-drill-body">
          <div className="bb-dsec">
            <div className="bb-dsec-h">Rendering</div>
            <div className="bb-cardwrap">
              {target.metric
                ? <MetricCard metric={target.metric} moduleKey={target.moduleKey} customerId={customerId} onDrill={() => {}} onEdit={() => {}} />
                : <div className="bb-bigval">{target.value}</div>}
            </div>
            <div className="bb-bigsub">scoped {target.node} · {loading ? 'loading…' : `${total.toLocaleString()} rows in detail below`}</div>
          </div>
          <div className="bb-dsec">
            <div className="bb-dsec-h">Export</div>
            <div className="bb-export">
              <button className="bb-xbtn primary" onClick={exportCsv} disabled={!rows.length}>↓ CSV</button>
              <button className="bb-xbtn" onClick={exportExcel} disabled={!rows.length}>↓ Excel</button>
            </div>
            <div className="bb-bigsub" style={{ marginTop: 9 }}>Provenance travels with the export — the SQL, sources and scope go with the number.</div>
          </div>
          <div className="bb-dsec">
            <div className="bb-dsec-h">Detail · underlying rows</div>
            {loading ? <div className="bb-note">Loading rows…</div> : rows.length === 0 ? <div className="bb-note">No row detail for this metric.</div> : (
              <table className="bb-tbl">
                <thead><tr>{columns.slice(0, 5).map(c => <th key={c}>{c}</th>)}</tr></thead>
                <tbody>{rows.slice(0, 8).map((r, i) => <tr key={i}>{columns.slice(0, 5).map(c => <td key={c}>{String(r[c])}</td>)}</tr>)}</tbody>
              </table>
            )}
          </div>
        </div>
      </aside>
    </>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function SpineView({ customerId, companyName = null, logoBase64 = null, activeFilters = {}, filterData = null }) {
  const [catalog, setCatalog] = useState(null)
  // Each read that failed, by name. A failed read is shown with a Retry -- it used to
  // become "no domains / no systems / no lanes", an empty backbone (item 201).
  const [loadErrors, setLoadErrors] = useState({})
  const [loadTry, setLoadTry] = useState(0)
  const noteLoad = (what, err) => setLoadErrors(prev => {
    const next = { ...prev }
    if (err) next[what] = loadErrorMessage(err)
    else delete next[what]
    return next
  })
  const [results, setResults] = useState({ hcm: {}, wfm: {}, finance: {} })
  const [lastRun, setLastRun] = useState({ hcm: null, wfm: null, finance: null })
  const [entityOrgMap, setEntityOrgMap] = useState({})
  const [selectedNode, setSelectedNode] = useState({ level: 'enterprise', code: null, label: null })
  const [mode, setMode] = useState('backbone')          // 'backbone' | 'metrics'
  const [explore, setExplore] = useState({ hcm: false, wfm: false, finance: false })
  const [expanded, setExpanded] = useState({})          // moduleKey -> open
  const [drill, setDrill] = useState(null)
  const [backbone, setBackbone] = useState(null)   // derived: present_domains, edges, findings
  const [systems, setSystems] = useState([])       // connected sources + functions (context, connection_status derived)
  const [spineLevels, setSpineLevels] = useState([])   // authored spine-level overrides ([] => derive)
  const [terminology, setTerminology] = useState({})   // authored label vocabulary (label_ref -> display)
  const [editLevel, setEditLevel] = useState(null)     // spine level key being authored ([] editor)
  const [draftLabel, setDraftLabel] = useState('')
  const [draftActive, setDraftActive] = useState(true)
  const [savingSpine, setSavingSpine] = useState(false)
  const [rolesByEntity, setRolesByEntity] = useState({})  // entityKey ('' = company-wide) -> [{code,title,people}]
  const [rolesLoading, setRolesLoading] = useState(false)
  const [roleQuery, setRoleQuery] = useState('')        // role picker search
  const [people, setPeople] = useState({})              // orgCode -> [{id,name,title,entity,status}] (cached per org)
  const [peopleLoading, setPeopleLoading] = useState(false)
  const [personQuery, setPersonQuery] = useState('')    // person picker search
  const [rollups, setRollups] = useState({})            // scopeKey -> [{key,label,pair,unit,value,formula,components}]
  const [rollupsLoading, setRollupsLoading] = useState(false)
  const [lanes, setLanes] = useState(null)              // [{key,label,grain,products,contracted,gap,...}]

  const abortRef = useRef({})
  const prevScopeRef = useRef(null)

  const entLabel = companyName || deriveEnterpriseLabel(customerId)

  // Org-node scoping context: which system built the tree (WFM worked-hours nesting
  // when present, else the HCM-derived entity_org_map) plus the backbone's name-matched
  // department crosswalk indices. nodeScope uses these to scope the non-native domain.
  const orgMaps = useMemo(() => ({
    system: (entityOrgMap && Object.keys(entityOrgMap).length) ? 'wfm' : 'hcm',
    wfmToHcm: backbone?.org_wfm_to_hcm || {},
    hcmToWfm: backbone?.org_hcm_to_wfm || {},
  }), [entityOrgMap, backbone])

  const scope = useMemo(() => ({
    ...(buildEffectiveFilters(activeFilters, filterData) || {}),
    ...nodeScope(selectedNode, backbone?.entity_finance_map, orgMaps),
  }), [activeFilters, filterData, selectedNode, backbone, orgMaps])
  const scopeKey = JSON.stringify(scope)

  const entities = useMemo(() => {
    const companyOpts = filterData?.filters?.company?.options || []
    const orgOpts = filterData?.filters?.org_level_1?.options || []
    const orgLabel = {}; orgOpts.forEach(o => { orgLabel[String(o.value)] = o.label || String(o.value) })
    // Prefer the WFM worked-hours nesting when present; fall back to the HCM-derived
    // entity_org_map (companyCode → orgLevel1Code) so HCM-only customers (no WFM) show
    // their real org counts instead of 0 orgs on every entity.
    const orgMap = (entityOrgMap && Object.keys(entityOrgMap).length) ? entityOrgMap : (backbone?.entity_org_map || {})
    return companyOpts.map(c => ({
      code: String(c.value), label: c.label || String(c.value), count: c.count,
      orgs: (orgMap[String(c.value)] || []).map(oc => ({ code: String(oc), label: orgLabel[String(oc)] || String(oc) })),
    }))
  }, [filterData, entityOrgMap, backbone])

  const runDomain = useCallback(async (domainKey, scopeOverride) => {
    if (!catalog) return
    const keys = groupModulesByDomain(catalog)[domainKey].flatMap(([, mod]) => Object.keys(mod.metrics || {}))
    if (!keys.length) return
    const effectiveFilters = scopeOverride !== undefined ? scopeOverride : { ...(buildEffectiveFilters(activeFilters, filterData) || {}), ...nodeScope(selectedNode, backbone?.entity_finance_map, orgMaps) }
    setResults(r => ({ ...r, [domainKey]: { ...(r[domainKey] || {}), ...Object.fromEntries(keys.map(k => [k, { ...(r[domainKey]?.[k] || {}), key: k, _loading: true }])) } }))
    if (abortRef.current[domainKey]) abortRef.current[domainKey].abort()
    const controller = new AbortController(); abortRef.current[domainKey] = controller
    try {
      await runMetricsStream(customerId, keys, effectiveFilters, (metric) => {
        if (controller.signal.aborted) return
        setResults(r => ({ ...r, [domainKey]: { ...(r[domainKey] || {}), [metric.key]: metric } }))
      }, controller.signal)
      if (!controller.signal.aborted) setLastRun(lr => ({ ...lr, [domainKey]: new Date() }))
    } catch (err) {
      if (err.name === 'AbortError') return
      try {
        const data = await runMetrics(customerId, keys, effectiveFilters)
        setResults(r => { const next = { ...(r[domainKey] || {}) }; (data || []).forEach(m => { next[m.key] = m }); return { ...r, [domainKey]: next } })
      } catch (e) { console.error(`Backbone run failed (${domainKey}):`, e) }
    }
  }, [catalog, customerId, activeFilters, filterData, selectedNode, backbone, orgMaps])

  useEffect(() => {
    let cancelled = false
    getMetricCatalog().then(cat => {
      if (cancelled) return
      setCatalog(cat)
      noteLoad('the metric catalog', null)
    }).catch(err => { console.error(err); if (!cancelled) noteLoad('the metric catalog', err) })
    getBackbone(customerId)
      .then(bb => { if (!cancelled) { setBackbone(bb); noteLoad('the data backbone', null) } })
      .catch(err => { if (!cancelled) { setBackbone({ present_domains: [], edges: [], findings: [] }); noteLoad('the data backbone', err) } })
    getCustomer(customerId)
      .then(ctx => {
        if (cancelled) return
        setSystems(ctx?.systems || [])
        setSpineLevels(ctx?.spine_levels || [])
        setTerminology(ctx?.terminology || {})
        noteLoad('the customer context', null)
      })
      .catch(err => { if (!cancelled) { setSystems([]); setSpineLevels([]); setTerminology({}); noteLoad('the customer context', err) } })
    return () => { cancelled = true; Object.values(abortRef.current).forEach(c => c && c.abort()) }
  }, [customerId, loadTry]) // eslint-disable-line react-hooks/exhaustive-deps

  // Once catalog AND the derived backbone are loaded, run only the PRESENT domains.
  // Gating the run on present_domains means HCM-only customers never run (or render)
  // phantom WFM/Finance — the data-present floor, from real data. Must fire from a
  // catalog-dependent effect (see the null-closure bug fix that moved this out of the fetch .then).
  useEffect(() => {
    if (!catalog || !backbone) return
    prevScopeRef.current = scopeKey
    ;(backbone.present_domains || []).forEach(k => runDomain(k, scope))
  }, [catalog, backbone]) // eslint-disable-line react-hooks/exhaustive-deps

  // The entity -> org map from the time system's own org paths. A failed read (an HTTP
  // error, or the drill answering with an `error`) is shown with the other load errors
  // and retried with them -- it used to be swallowed, and the tree silently fell back to
  // the HCM map. A drill that answers with no rows (no time data synced) is not a failure.
  useEffect(() => {
    let cancelled = false
    drillMetric(customerId, TREE_DRILL_METRIC, 10000, null, null).then(res => {
      if (cancelled) return
      if (res?.error) throw new Error(res.error)
      const rows = listFrom(res, 'rows')
      const map = {}
      rows.forEach(row => { const e = row.entity != null ? String(row.entity) : null, o = row.org != null ? String(row.org) : null; if (!e || !o) return; (map[e] = map[e] || new Set()).add(o) })
      const out = {}; Object.entries(map).forEach(([e, set]) => { out[e] = [...set].sort((a, b) => (Number(a) || 0) - (Number(b) || 0)) })
      setEntityOrgMap(out)
      noteLoad('the entity-to-org map', null)
    }).catch(err => { if (!cancelled) noteLoad('the entity-to-org map', err) })
    return () => { cancelled = true }
  }, [customerId, loadTry])

  useEffect(() => {
    if (!catalog) return
    if (prevScopeRef.current === null) { prevScopeRef.current = scopeKey; return }
    if (prevScopeRef.current === scopeKey) return
    prevScopeRef.current = scopeKey
    const t = setTimeout(() => { (backbone?.present_domains || []).forEach(k => runDomain(k, scope)) }, 300)
    return () => clearTimeout(t)
  }, [scopeKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Authored-wins resolution of spine levels (labels + which levels are active),
  // layered over the DERIVED per-system alignment from the backbone.
  const resolvedLevels = useMemo(
    () => resolveLevels(LEVELS, spineLevels, terminology, backbone?.spine_alignment),
    [spineLevels, terminology, backbone]
  )

  // Build the spine node list. Authored-inactive levels are dropped from the trunk;
  // labels come from resolvedLevels (derived eyebrow unless a label_ref is authored).
  const nodes = useMemo(() => {
    const RL = resolvedLevels
    const sys = (k) => ({ systems: RL[k].systems, agree: RL[k].agree })
    const list = [{ key: 'enterprise', eyebrow: RL.enterprise.label, title: entLabel, code: null, ...sys('enterprise') }]
    if (RL.entity.active) {
      const entCode = selectedNode.level === 'entity' ? selectedNode.code
        : selectedNode.level === 'org' ? selectedNode.parent
        : selectedNode.level === 'person' ? selectedNode.gp
        : selectedNode.level === 'role' ? selectedNode.entity   // composed entity×role
        : null
      if (entCode) {
        const ent = entities.find(e => e.code === entCode)
        list.push({ key: 'entity', eyebrow: RL.entity.label, title: ent ? ent.label : entCode, code: ent ? ent.code : entCode, ...sys('entity') })
      } else {
        list.push({ key: 'entity', eyebrow: RL.entity.label, title: `${entities.length} entities`, code: null, browse: true, ...sys('entity') })
      }
    }
    if (RL.org.active) {
      if (selectedNode.level === 'org') list.push({ key: 'org', eyebrow: RL.org.label, title: selectedNode.label, code: selectedNode.code, parent: selectedNode.parent, ...sys('org') })
      else if (selectedNode.level === 'person') list.push({ key: 'org', eyebrow: RL.org.label, title: selectedNode.orgLabel || selectedNode.parent, code: selectedNode.parent, parent: selectedNode.gp, ...sys('org') })
      else list.push({ key: 'org', eyebrow: RL.org.label, title: 'select an entity', code: null, ...sys('org') })
    }
    if (RL.role.active) {
      if (selectedNode.level === 'role' && selectedNode.code) list.push({ key: 'role', eyebrow: RL.role.label, title: selectedNode.label, code: selectedNode.code, ...sys('role') })
      else list.push({ key: 'role', eyebrow: RL.role.label, title: 'Canonical roles', code: null, ...sys('role') })
    }
    if (RL.person.active) {
      if (selectedNode.level === 'person' && selectedNode.code) list.push({ key: 'person', eyebrow: RL.person.label, title: selectedNode.label, code: selectedNode.code, ...sys('person') })
      else list.push({ key: 'person', eyebrow: RL.person.label, title: 'Person', code: null, ...sys('person') })
    }
    return list
  }, [entLabel, entities, selectedNode, resolvedLevels])

  // The entity in context for the Role node ('' = company-wide). Role composes
  // entity×role: within an entity, roles + headcounts are entity-scoped.
  const roleEntity = selectedNode.level === 'role' ? (selectedNode.entity || '') : ''

  // Role picker: filter the codes by title or code, cap the rendered set.
  const ROLE_CAP = 60
  const filteredRoles = useMemo(() => {
    const all = rolesByEntity[roleEntity] || []
    const q = roleQuery.trim().toLowerCase()
    const matched = q
      ? all.filter(r => (r.title || '').toLowerCase().includes(q) || String(r.code).toLowerCase().includes(q))
      : all
    return { shown: matched.slice(0, ROLE_CAP), total: matched.length, loaded: rolesByEntity[roleEntity] !== undefined }
  }, [rolesByEntity, roleEntity, roleQuery])

  // The HCM org code to roster people for — crosswalk-resolved (scope.org_level_1)
  // so a WFM-built tree with differing codes still lands on the right HCM unit.
  const personOrgCode = selectedNode.level === 'org' ? (scope.org_level_1?.[0] || selectedNode.code) : null

  // Person picker: filter the org's roster by name / title / id, cap the render.
  const PERSON_CAP = 60
  const filteredPeople = useMemo(() => {
    const all = (personOrgCode ? people[personOrgCode] : null) || []
    const q = personQuery.trim().toLowerCase()
    const matched = q
      ? all.filter(p => (p.name || '').toLowerCase().includes(q) || (p.title || '').toLowerCase().includes(q) || String(p.id).toLowerCase().includes(q))
      : all
    return { shown: matched.slice(0, PERSON_CAP), total: matched.length, loaded: personOrgCode ? people[personOrgCode] !== undefined : false }
  }, [people, personOrgCode, personQuery])

  // Lazy-load (and cache per entity) roles when the Role node is entered.
  useEffect(() => {
    if (selectedNode.level !== 'role' || rolesByEntity[roleEntity] !== undefined || rolesLoading) return
    let alive = true
    setRolesLoading(true)
    getRoles(customerId, roleEntity || undefined)
      .then(r => { if (alive) setRolesByEntity(prev => ({ ...prev, [roleEntity]: (r && r.roles) || [] })) })
      .catch(() => { if (alive) setRolesByEntity(prev => ({ ...prev, [roleEntity]: [] })) })
      .finally(() => { if (alive) setRolesLoading(false) })
    return () => { alive = false }
  }, [selectedNode.level, roleEntity, rolesByEntity, rolesLoading, customerId])

  // Role search resets when the entity context changes.
  useEffect(() => { setRoleQuery('') }, [roleEntity])

  // Lazy-load (and cache per org) the roster for the person picker on an Org node.
  useEffect(() => {
    if (selectedNode.level !== 'org' || !personOrgCode || people[personOrgCode] !== undefined || peopleLoading) return
    let alive = true
    setPeopleLoading(true)
    getPeople(customerId, personOrgCode)
      .then(r => { if (alive) setPeople(prev => ({ ...prev, [personOrgCode]: (r && r.people) || [] })) })
      .catch(() => { if (alive) setPeople(prev => ({ ...prev, [personOrgCode]: [] })) })
      .finally(() => { if (alive) setPeopleLoading(false) })
    return () => { alive = false }
  }, [selectedNode.level, personOrgCode, customerId, peopleLoading, people])

  // Person search is per-org — clear it when the org context changes.
  useEffect(() => { setPersonQuery('') }, [personOrgCode])

  // §9 cross-domain rollups — composed server-side from proven metrics, scoped to
  // the current node. Only meaningful with 2+ domains; cached + debounced by scope.
  useEffect(() => {
    const present = backbone?.present_domains || []
    if (present.length < 2 || rollups[scopeKey] !== undefined) return
    let alive = true
    setRollupsLoading(true)
    const t = setTimeout(() => {
      getRollups(customerId, scope)
        .then(r => { if (alive) setRollups(prev => ({ ...prev, [scopeKey]: (r && r.rollups) || [] })) })
        .catch(() => { if (alive) setRollups(prev => ({ ...prev, [scopeKey]: [] })) })
        .finally(() => { if (alive) setRollupsLoading(false) })
    }, 300)
    return () => { alive = false; clearTimeout(t) }
  }, [scopeKey, backbone, customerId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Capability lanes — scope-aware. The systems view reshapes to the current node:
  // Enterprise shows the full inventory + contracted gaps; drill to an entity/org and
  // coverage re-computes to that population, showing only the products it actually uses.
  useEffect(() => {
    let alive = true
    const t = setTimeout(() => {
      getLanes(customerId, scope)
        .then(l => {
          if (!alive) return
          setLanes((l && l.lanes) || [])
          noteLoad('capability lanes', null)
          // the lanes answered, but the contracted product keys could not be read
          noteLoad('the contracted products', l && l.contracted_error ? new Error(l.contracted_error) : null)
        })
        .catch(err => { if (alive) { setLanes([]); noteLoad('capability lanes', err) } })
    }, 200)
    return () => { alive = false; clearTimeout(t) }
  }, [scopeKey, customerId, loadTry]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSelectNode = (n) => {
    if (n.key === 'enterprise') setSelectedNode({ level: 'enterprise', code: null, label: entLabel })
    else if (n.key === 'entity' && n.code) setSelectedNode({ level: 'entity', code: n.code, label: n.title })
    else if (n.key === 'org' && n.code) setSelectedNode({ level: 'org', code: n.code, label: n.title, parent: n.parent !== undefined ? n.parent : selectedNode.parent })
    // Clicking the Role node enters role-browse (no specific code yet → the
    // searchable picker shows). It carries the entity in context so Role composes
    // entity×role; from Enterprise it's company-wide. Picking a role keeps entity.
    else if (n.key === 'role') {
      const entity = selectedNode.level === 'entity' ? selectedNode.code
        : selectedNode.level === 'org' ? selectedNode.parent
        : selectedNode.level === 'person' ? selectedNode.gp
        : selectedNode.level === 'role' ? selectedNode.entity
        : null
      const entityLabel = entity ? (entities.find(e => e.code === entity)?.label || entity) : null
      setSelectedNode({ level: 'role', code: null, label: 'Canonical roles', entity: entity || null, entityLabel })
    }
  }

  // ── Spine-level authoring (context authoring — extends this surface) ─────────
  // The consultant pins the canonical label per level (label_ref -> terminology)
  // or toggles a level active. Derived alignment is the floor; authored wins.
  const reloadContext = useCallback(async () => {
    try {
      const ctx = await getCustomer(customerId)
      setSpineLevels(ctx?.spine_levels || [])
      setTerminology(ctx?.terminology || {})
    } catch (e) { console.error('reload context failed:', e) }
  }, [customerId])

  const beginEdit = (key) => {
    const authored = (spineLevels || []).find(sl => sl.level === key)
    const curLabel = authored && authored.label_ref ? (terminology[authored.label_ref] || '') : ''
    setEditLevel(key)
    setDraftLabel(curLabel)
    setDraftActive(resolvedLevels[key] ? resolvedLevels[key].active : true)
  }
  const cancelEdit = () => { setEditLevel(null); setDraftLabel(''); setDraftActive(true) }

  const saveSpine = async (key) => {
    setSavingSpine(true)
    try {
      const label = (draftLabel || '').trim()
      const active = key === 'enterprise' ? true : draftActive
      const others = (spineLevels || []).filter(sl => sl.level !== key)
      let labelRef = null
      if (label) {
        labelRef = `spine_${key}`
        await addTerminology(customerId, labelRef, label)   // pin the label in terminology
      }
      // only carry an entry when it changes something (custom label OR hidden level)
      const next = (label || active === false) ? [...others, { level: key, active, label_ref: labelRef }] : others
      await apiSetSpineLevels(customerId, next)
      await reloadContext()
      cancelEdit()
    } catch (e) { console.error('save spine level failed:', e) } finally { setSavingSpine(false) }
  }
  const revertSpine = async (key) => {
    setSavingSpine(true)
    try {
      const next = (spineLevels || []).filter(sl => sl.level !== key)
      await apiSetSpineLevels(customerId, next)
      await reloadContext()
      cancelEdit()
    } catch (e) { console.error('revert spine level failed:', e) } finally { setSavingSpine(false) }
  }

  const domScalars = (dk) => {
    const out = []
    for (const [mk, mod] of groupModulesByDomain(catalog)[dk]) {
      for (const [key, def] of Object.entries(mod.metrics || {})) {
        const r = results[dk]?.[key]
        const disp = r?.display_type || def.display_type
        if (r && r.value != null && !r.error && ['number', 'currency', 'percent'].includes(disp)) {
          out.push({ key, label: r.label || def.label || key, value: fmtVal(r.value, disp), disp, module: mod.label, moduleKey: mk, tint: DOMAINS.find(d => d.key === dk).tint, domName: DOMAINS.find(d => d.key === dk).name })
          if (out.length >= 4) return out
        }
      }
    }
    return out
  }
  const openDrill = (dk, moduleKey, moduleLabel, key) => {
    const def = catalog[moduleKey]?.metrics?.[key] || {}
    const r = results[dk]?.[key] || {}
    const disp = r.display_type || def.display_type
    setDrill({ key, label: r.label || def.label || key, value: fmtVal(r.value, disp), display: disp, domain: DOMAINS.find(d => d.key === dk).name, tint: DOMAINS.find(d => d.key === dk).tint, module: moduleLabel, node: selectedNode.label || entLabel, metric: { ...def, ...r, key, module_key: moduleKey }, moduleKey })
  }

  if (!catalog) return <div className="bb-loading"><div className="bb-spin" /> Loading Backbone…</div>

  const grouped = groupModulesByDomain(catalog)
  const visibleDomains = DOMAINS.filter(d => (backbone?.present_domains || []).includes(d.key))
  const edges = backbone?.edges || []
  const findings = backbone?.findings || []
  const curEnt = entities.find(e => e.code === (selectedNode.level === 'org' ? selectedNode.parent : selectedNode.code))
  const curOrgs = curEnt?.orgs || []
  const nodeTitle = selectedNode.level === 'enterprise' ? entLabel : selectedNode.label
  const scopePill = selectedNode.level === 'enterprise'
    ? `${resolvedLevels.enterprise.label} · unscoped`
    : selectedNode.level === 'role'
      ? `${resolvedLevels.role.label} · ${selectedNode.entity ? selectedNode.entity + ' › ' : ''}${selectedNode.code || 'select one'}`
      : selectedNode.level === 'person'
        ? `${resolvedLevels.person.label} · in ${selectedNode.parent || '—'}`
        : `${selectedNode.level === 'entity' ? resolvedLevels.entity.label : resolvedLevels.org.label} · ${selectedNode.code}`

  return (
    <div className="bb-root">
      {Object.entries(loadErrors).map(([what, err]) => (
        <LoadError key={what} what={what} error={err} onRetry={() => setLoadTry(n => n + 1)} />
      ))}
      <div className="bb-topbar">
        <div className="bb-brand">
          {logoBase64
            ? <img className="bb-logo" src={logoBase64.startsWith('data:') ? logoBase64 : `data:image/png;base64,${logoBase64}`} alt={entLabel} />
            : <span className="bb-logo-fallback">{(entLabel || '?').slice(0, 1)}</span>}
          <span className="bb-company">{entLabel}</span>
          <span className="bb-eyebrow-tag">Operating-model backbone</span>
        </div>
        <div className="bb-mode">
          <button className={`bb-mode-btn ${mode === 'backbone' ? 'on' : ''}`} onClick={() => setMode('backbone')}>Backbone</button>
          <button className={`bb-mode-btn ${mode === 'metrics' ? 'on' : ''}`} onClick={() => setMode('metrics')}>Metrics</button>
        </div>
      </div>

      {mode === 'metrics' ? (
        <div className="bb-metrics-direct">
          <div className="bb-md-head">
            <div className="bb-crumb"><b>Metrics</b> · direct door</div>
            <div className="bb-md-title">Every metric — straight in</div>
            <div className="bb-md-sub">The other door: all metrics, no backbone walk. Same live data — flat and immediate. Open a metric to drill.</div>
          </div>
          <div className="bb-md-grid">
            {visibleDomains.length === 0 && <div className="bb-note">{backbone ? 'No connected domains for this customer yet.' : 'Resolving connected domains…'}</div>}
            {visibleDomains.map(d => (
              <div key={d.key} className="bb-mdcol">
                <div className="bb-mdom-h"><span className="bb-dot" style={{ background: d.tint }} /><span className="bb-dom-n">{d.name}</span></div>
                {grouped[d.key].map(([mk, mod]) => (
                  <div key={mk} className={`bb-mmod ${expanded['x' + mk] ? 'open' : ''}`}>
                    <div className="bb-mmod-h" onClick={() => setExpanded(e => ({ ...e, ['x' + mk]: !e['x' + mk] }))}>
                      <span className="bb-chev">▶</span><span className="bb-mic">{mod.icon}</span><span className="bb-ml">{mod.label}</span>
                    </div>
                    <div className="bb-mmets">
                      {Object.entries(mod.metrics || {}).map(([key, def]) => {
                        const r = results[d.key]?.[key]
                        return <div key={key} className="bb-mmet" onClick={() => openDrill(d.key, mk, mod.label, key)}>
                          <span className="bb-mdot" /><span className="bb-mml">{r?.label || def.label || key}</span>
                          <span className="bb-mmv">{fmtVal(r?.value, r?.display_type || def.display_type)}</span><span className="bb-drilltag">drill ›</span></div>
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bb-stage">
          <div className="bb-spinewrap"><Backbone nodes={nodes} selected={selectedNode} onSelect={onSelectNode} lanes={lanes} /></div>
          <div className="bb-panel">
            <div className="bb-p-head">
              <div className="bb-crumb">
                <span className="bb-clink" onClick={() => onSelectNode({ key: 'enterprise' })}>{entLabel}</span>
                {selectedNode.level !== 'enterprise' && <> <span className="bb-csep">›</span> <b>{nodeTitle}</b></>}
              </div>
              <div className="bb-p-title">{nodeTitle}</div>
              <span className="bb-scope">{scopePill}</span>
            </div>

            {selectedNode.level === 'enterprise' && resolvedLevels.entity.active && entities.length > 0 && (
              <div className="bb-sect">
                <div className="bb-sect-h">{resolvedLevels.entity.authored ? resolvedLevels.entity.label : 'Subsidiaries'} <span className="bb-hint">— drop in to scope everything to it</span></div>
                <div className="bb-picker">
                  {entities.map(e => (
                    <button key={e.code} className="bb-pick" onClick={() => setSelectedNode({ level: 'entity', code: e.code, label: e.label, parent: e.code })}>
                      <div className="bb-pick-top"><span className="bb-pick-name">{e.label}</span><span className="bb-pick-code">{e.code}</span></div>
                      <div className="bb-pick-meta">{e.count ? `${e.count.toLocaleString()} people · ` : ''}{e.orgs.length} org{e.orgs.length === 1 ? '' : 's'}<span className="bb-pick-arrow">↓</span></div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {selectedNode.level === 'entity' && resolvedLevels.org.active && (
              <div className="bb-sect">
                <div className="bb-sect-h">{resolvedLevels.org.authored ? resolvedLevels.org.label : 'Org units'} <span className="bb-hint">— {curOrgs.length} under {selectedNode.code}</span></div>
                <div className="bb-picker">
                  {curOrgs.map(o => (
                    <button key={o.code} className="bb-pick" onClick={() => setSelectedNode({ level: 'org', code: o.code, label: o.label, parent: selectedNode.code })}>
                      <div className="bb-pick-top"><span className="bb-pick-name">{o.label}</span><span className="bb-pick-code">{o.code}</span></div>
                      <div className="bb-pick-meta">org unit<span className="bb-pick-arrow">↓</span></div>
                    </button>
                  ))}
                  {curOrgs.length === 0 && <div className="bb-note">Deriving org units…</div>}
                </div>
              </div>
            )}
            {selectedNode.level === 'org' && resolvedLevels.person.active && (
              <div className="bb-sect">
                <div className="bb-sect-h">
                  Down to {resolvedLevels.person.label}
                  <span className="bb-hint">— drill into an individual in {selectedNode.code}</span>
                </div>
                {!personOrgCode && <div className="bb-note" style={{ textAlign: 'left', padding: '4px 2px' }}>This org needs the HCM crosswalk to list its people.</div>}
                {personOrgCode && (
                  <>
                    <input
                      className="bb-lvl-input"
                      style={{ width: '100%', marginBottom: '10px' }}
                      value={personQuery}
                      onChange={e => setPersonQuery(e.target.value)}
                      placeholder={peopleLoading ? 'Loading people…' : `Search ${((people[personOrgCode]) || []).length} people by name, title, or ID…`}
                    />
                    {peopleLoading && <div className="bb-note">Loading the roster for {selectedNode.code}…</div>}
                    {!peopleLoading && filteredPeople.loaded && filteredPeople.total === 0 && (people[personOrgCode] || []).length === 0 && <div className="bb-note">No people found in this org.</div>}
                    {!peopleLoading && (people[personOrgCode] || []).length > 0 && (
                      <>
                        <div className="bb-picker">
                          {filteredPeople.shown.map(p => (
                            <button
                              key={p.id}
                              className="bb-pick"
                              onClick={() => setSelectedNode({ level: 'person', code: p.id, label: p.name || p.id, parent: selectedNode.code, orgLabel: selectedNode.label, gp: selectedNode.parent, title: p.title, entity: p.entity, status: p.status })}
                            >
                              <div className="bb-pick-top">
                                <span className="bb-pick-name">{p.name || '—'}</span>
                                <span className="bb-pick-code">{p.status === 'A' ? 'active' : (p.status || '—')}</span>
                              </div>
                              <div className="bb-pick-meta">{p.title || '—'}<span className="bb-pick-arrow">↓</span></div>
                            </button>
                          ))}
                        </div>
                        {filteredPeople.total > filteredPeople.shown.length && (
                          <div className="bb-note" style={{ textAlign: 'left', padding: '8px 2px 0' }}>
                            Showing {filteredPeople.shown.length} of {filteredPeople.total} matches — refine your search to narrow.
                          </div>
                        )}
                        {filteredPeople.total === 0 && <div className="bb-note">No people match “{personQuery}”.</div>}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
            {selectedNode.level === 'person' && resolvedLevels.person.active && (
              <div className="bb-sect">
                <div className="bb-sect-h">
                  {resolvedLevels.person.label}
                  <span className="bb-hint">— drill-only; metrics below stay scoped to {selectedNode.parent}</span>
                </div>
                <div className="bb-edge-card" style={{ borderLeftColor: '#42b8fc' }}>
                  <div className="bb-ec-top">
                    <span className="bb-ec-name">{selectedNode.label}</span>
                    <span className="bb-chip" style={{ background: '#e2f2fb', color: '#1f9bd8' }}>{selectedNode.status === 'A' ? 'active' : (selectedNode.status || '—')}</span>
                  </div>
                  <div className="bb-ec-lead">{selectedNode.title || '—'}{selectedNode.entity ? ` · ${selectedNode.entity}` : ''}</div>
                  <div className="bb-ec-keys">employeeID {selectedNode.code} · org {selectedNode.parent}</div>
                </div>
                <div className="bb-note" style={{ textAlign: 'left', padding: '8px 2px 0' }}>
                  <span className="bb-clink" onClick={() => setSelectedNode({ level: 'org', code: selectedNode.parent, label: selectedNode.orgLabel || selectedNode.parent, parent: selectedNode.gp })}>‹ back to {selectedNode.orgLabel || selectedNode.parent}</span>
                </div>
              </div>
            )}
            {selectedNode.level === 'role' && resolvedLevels.role.active && (
              <div className="bb-sect">
                <div className="bb-sect-h">
                  {resolvedLevels.role.authored ? resolvedLevels.role.label : 'Canonical roles'}
                  <span className="bb-hint">— one job code scopes HCM + WFM{selectedNode.entity ? ` · within ${selectedNode.entityLabel || selectedNode.entity}` : ' · company-wide'}</span>
                </div>
                <input
                  className="bb-lvl-input"
                  style={{ width: '100%', marginBottom: '10px' }}
                  value={roleQuery}
                  onChange={e => setRoleQuery(e.target.value)}
                  placeholder={rolesLoading ? 'Loading roles…' : `Search ${(rolesByEntity[roleEntity] || []).length} roles by title or code…`}
                />
                {rolesLoading && <div className="bb-note">Deriving canonical roles…</div>}
                {!rolesLoading && filteredRoles.loaded && (rolesByEntity[roleEntity] || []).length === 0 && <div className="bb-note">No role grain{selectedNode.entity ? ` in ${selectedNode.entityLabel || selectedNode.entity}` : ' for this customer'}.</div>}
                {!rolesLoading && (rolesByEntity[roleEntity] || []).length > 0 && (
                  <>
                    <div className="bb-picker">
                      {filteredRoles.shown.map(r => {
                        const on = selectedNode.code === r.code
                        return (
                          <button
                            key={r.code}
                            className="bb-pick"
                            style={on ? { borderColor: '#42b8fc', background: '#f5fbff' } : undefined}
                            onClick={() => setSelectedNode({ level: 'role', code: r.code, label: r.title || r.code, entity: selectedNode.entity || null, entityLabel: selectedNode.entityLabel || null })}
                          >
                            <div className="bb-pick-top">
                              <span className="bb-pick-name">{r.title || '—'}</span>
                              <span className="bb-pick-code">{r.code}</span>
                            </div>
                            <div className="bb-pick-meta">{r.people.toLocaleString()} {r.people === 1 ? 'person' : 'people'}<span className="bb-pick-arrow">{on ? '✓' : '↓'}</span></div>
                          </button>
                        )
                      })}
                    </div>
                    {filteredRoles.total > filteredRoles.shown.length && (
                      <div className="bb-note" style={{ textAlign: 'left', padding: '8px 2px 0' }}>
                        Showing {filteredRoles.shown.length} of {filteredRoles.total} matches — refine your search to narrow.
                      </div>
                    )}
                    {filteredRoles.total === 0 && <div className="bb-note">No role matches “{roleQuery}”.</div>}
                  </>
                )}
              </div>
            )}


            {selectedNode.level === 'enterprise' && (
              <div className="bb-sect">
                <div className="bb-sect-h">Operating-model labels <span className="bb-hint">— author the canonical name per level; derived is the floor</span></div>
                <div className="bb-lvledit">
                  {LEVELS.map(({ key, eyebrow }) => {
                    const rl = resolvedLevels[key] || { label: eyebrow, active: true, authored: false }
                    const editing = editLevel === key
                    const derived = ((backbone?.spine_alignment || []).find(a => a.level === key)?.headline) || eyebrow
                    return (
                      <div key={key} className={`bb-lvl ${rl.active ? '' : 'off'}`}>
                        <div className="bb-lvl-main">
                          <span className="bb-lvl-eyebrow">{eyebrow}</span>
                          <span className="bb-lvl-label">{rl.label}</span>
                          {rl.authored && <span className="bb-lvl-badge">authored</span>}
                          {!rl.active && <span className="bb-lvl-badge off">hidden</span>}
                          {!editing && <button className="bb-lvl-edit" onClick={() => beginEdit(key)} aria-label={`Edit ${eyebrow} label`}>✎</button>}
                        </div>
                        {!editing
                          ? <div className="bb-lvl-derived">derived floor: {derived}</div>
                          : (
                            <div className="bb-lvl-form">
                              <input className="bb-lvl-input" value={draftLabel} onChange={e => setDraftLabel(e.target.value)}
                                placeholder={`Label — blank reverts to derived “${derived}”`} />
                              {key !== 'enterprise' && (
                                <label className="bb-lvl-toggle">
                                  <input type="checkbox" checked={draftActive} onChange={e => setDraftActive(e.target.checked)} /> Active level
                                </label>
                              )}
                              <div className="bb-lvl-btns">
                                <button className="bb-lvl-save" disabled={savingSpine} onClick={() => saveSpine(key)}>{savingSpine ? 'Saving…' : 'Save'}</button>
                                {rl.authored && <button className="bb-lvl-revert" disabled={savingSpine} onClick={() => revertSpine(key)}>Revert</button>}
                                <button className="bb-lvl-cancel" disabled={savingSpine} onClick={cancelEdit}>Cancel</button>
                              </div>
                            </div>
                          )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {(backbone?.present_domains || []).length >= 2 && (
              <div className="bb-sect">
                <div className="bb-sect-h">Cross-domain <span className="bb-asserted">composed · live</span></div>
                {rollupsLoading && rollups[scopeKey] === undefined && <div className="bb-note" style={{ textAlign: 'left', padding: '2px' }}>Composing cross-domain rollups…</div>}
                {rollups[scopeKey] !== undefined && (rollups[scopeKey] || []).length === 0 && <div className="bb-note" style={{ textAlign: 'left', padding: '2px' }}>No cross-domain rollup resolves for this scope.</div>}
                {(rollups[scopeKey] || []).map(rl => (
                  <div key={rl.key} className="bb-edge-card" style={{ borderLeftColor: '#7c5cf0' }}>
                    <div className="bb-ec-top">
                      <span className="bb-ec-name">{rl.label}</span>
                      <span className="bb-chip" style={{ background: '#efeafd', color: '#7c5cf0' }}>{(rl.pair || '').replace('×', ' × ').toUpperCase()}</span>
                    </div>
                    <div className="bb-ec-lead" style={{ fontSize: '17px', fontWeight: 700, color: '#031244' }}>
                      {fmtRollup(rl.value, rl.unit)}{ROLLUP_SUFFIX[rl.key] || ''}
                    </div>
                    <div className="bb-ec-keys">
                      {rl.formula} = {(rl.components || []).map(c => (c.metric === 'total_revenue' || c.metric === 'expense_by_account') ? fmtVal(c.value, 'currency') : fmtVal(c.value, 'number')).join('  ÷  ')}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="bb-sect">
              <div className="bb-sect-h">How this node aligns <span className="bb-asserted">derived · live</span></div>
              {edges.length === 0 && <div className="bb-note" style={{ textAlign: 'left', padding: '2px' }}>{backbone ? 'Single connected domain — no cross-domain crosswalk yet.' : 'Deriving crosswalks…'}</div>}
              {edges.map(e => {
                const st = STATE[e.state]
                return <div key={e.id} className="bb-edge-card" style={{ borderLeftColor: st.c }}>
                  <div className="bb-ec-top"><span className="bb-ec-name">{DOMAINS.find(d => d.key === e.a).name} ↔ {DOMAINS.find(d => d.key === e.b).name} · {e.sub}</span>
                    <span className="bb-chip" style={{ background: st.bg, color: st.c }}>{st.label}</span></div>
                  <div className="bb-ec-lead">{e.lead}</div>
                  <div className="bb-ec-keys">{e.keys[0]} ↔ {e.keys[1]}{e.metric ? ` · ${e.metric}` : ''}</div>
                </div>
              })}
            </div>

            <div className="bb-sect">
              <div className="bb-sect-h">Findings</div>
              {findings.map((f, i) => {
                const st = STATE[f.sev]
                return <div key={i} className="bb-finding" style={{ borderLeftColor: st.c }}>
                  <span className="bb-fic" style={{ background: st.bg, color: st.c }}>{f.sev === 'conflicting' ? '✕' : f.sev === 'proposed' ? '?' : '—'}</span>
                  <div><div className="bb-ft">{f.t}</div><div className="bb-fp">{f.p}</div></div></div>
              })}
            </div>

            <div className="bb-sect">
              <div className="bb-sect-h">Metrics <span className="bb-hint">glance → explore → drill</span></div>
              {visibleDomains.length === 0 && <div className="bb-note" style={{ textAlign: 'left', padding: '2px' }}>{backbone ? 'No connected domains for this customer yet.' : 'Resolving connected domains…'}</div>}
              {visibleDomains.map(d => {
                const head = domScalars(d.key)
                const open = explore[d.key]
                const scoped = selectedNode.level !== 'enterprise' && domainScoped(d.key, scope)
                const pending = selectedNode.level !== 'enterprise' && !scoped
                return (
                  <div key={d.key} className="bb-mdomain">
                    <div className="bb-mdom-h">
                      <span className="bb-dot" style={{ background: d.tint }} /><span className="bb-dom-n">{d.name}</span>
                      {scoped && <span className="bb-scoped-tag">scoped · {selectedNode.level === 'person' ? selectedNode.parent : selectedNode.code}</span>}
                      {pending && <span className="bb-pending-tag">Enterprise · crosswalk pending</span>}
                    </div>
                    <div className="bb-headline">
                      {head.length === 0 ? <div className="bb-note" style={{ gridColumn: '1/-1' }}>Running…</div> :
                        head.map(x => <div key={x.key} className="bb-htile" onClick={() => openDrill(d.key, x.moduleKey, x.module, x.key)}>
                          <div className="bb-hv">{x.value}</div><div className="bb-hl">{x.label}</div></div>)}
                    </div>
                    <button className={`bb-explore ${open ? 'on' : ''}`} onClick={() => setExplore(e => ({ ...e, [d.key]: !e[d.key] }))}>
                      {open ? '▾ Hide' : `→ Explore all ${grouped[d.key].reduce((a, [, m]) => a + Object.keys(m.metrics || {}).length, 0)} ${d.name} metrics`}
                    </button>
                    {open && (
                      <div className="bb-mtree">
                        {grouped[d.key].map(([mk, mod]) => (
                          <div key={mk} className={`bb-mmod ${expanded[mk] ? 'open' : ''}`}>
                            <div className="bb-mmod-h" onClick={() => setExpanded(e => ({ ...e, [mk]: !e[mk] }))}>
                              <span className="bb-chev">▶</span><span className="bb-mic">{mod.icon}</span><span className="bb-ml">{mod.label}</span></div>
                            <div className="bb-mmets">
                              {Object.entries(mod.metrics || {}).map(([key, def]) => {
                                const r = results[d.key]?.[key]
                                return <div key={key} className="bb-mmet" onClick={() => openDrill(d.key, mk, mod.label, key)}>
                                  <span className="bb-mdot" /><span className="bb-mml">{r?.label || def.label || key}</span>
                                  <span className="bb-mmv">{fmtVal(r?.value, r?.display_type || def.display_type)}</span><span className="bb-drilltag">drill ›</span></div>
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      <DrillDrawer customerId={customerId} target={drill} scope={scope} onClose={() => setDrill(null)} />
    </div>
  )
}
